import type { ComponentProps } from 'react';

import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { cn } from '@repo/lib/cn';

import type { ButtonProps } from '@/components/ui/button';

import { buttonVariants } from '@/components/ui/button';

export function AlertDialog(alertDialogProps: ComponentProps<typeof AlertDialogPrimitive.Root>) {
  const { ...props } = alertDialogProps;
  return <AlertDialogPrimitive.Root data-slot="alert-dialog" {...props} />;
}

export function AlertDialogTrigger(alertDialogTriggerProps: ComponentProps<typeof AlertDialogPrimitive.Trigger>) {
  const { ...props } = alertDialogTriggerProps;

  return <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />;
}

export function AlertDialogPortal(alertDialogPortalProps: ComponentProps<typeof AlertDialogPrimitive.Portal>) {
  const { ...props } = alertDialogPortalProps;

  return <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />;
}

export function AlertDialogOverlay(alertDialogOverlayProps: ComponentProps<typeof AlertDialogPrimitive.Overlay>) {
  const { className, ...props } = alertDialogOverlayProps;

  return (
    <AlertDialogPrimitive.Overlay
      data-slot="alert-dialog-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px]',
        className
      )}
      {...props}
    />
  );
}

export function AlertDialogContent(alertDialogContentProps: ComponentProps<typeof AlertDialogPrimitive.Content>) {
  const { className, ...props } = alertDialogContentProps;

  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        data-slot="alert-dialog-content"
        className={cn(
          'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 flex max-h-[calc(100%-2rem)] w-full max-w-[calc(100%-2rem)] grow translate-x-[-50%] translate-y-[-50%] flex-col overflow-hidden rounded-lg border shadow-lg duration-150 sm:max-w-5xl',
          className
        )}
        {...props}
      />
    </AlertDialogPortal>
  );
}

export function AlertDialogHeader(alertDialogHeaderProps: ComponentProps<'div'>) {
  const { className, ...props } = alertDialogHeaderProps;

  return <div data-slot="alert-dialog-header" className={cn('flex flex-row items-center gap-3 text-left p-4', className)} {...props} />;
}

export function AlertDialogFooter(alertDialogFooterProps: ComponentProps<'div'>) {
  const { className, ...props } = alertDialogFooterProps;

  return <div data-slot="alert-dialog-footer" className={cn('flex justify-end space-x-3 p-4', className)} {...props} />;
}

export function AlertDialogTitle(alertDialogTitleProps: ComponentProps<typeof AlertDialogPrimitive.Title>) {
  const { className, ...props } = alertDialogTitleProps;

  return <AlertDialogPrimitive.Title data-slot="alert-dialog-title" className={cn('mr-9 w-full truncate text-lg leading-none font-semibold tracking-tight whitespace-nowrap', className)} {...props} />;
}

export function AlertDialogDescription(alertDialogDescriptionProps: ComponentProps<typeof AlertDialogPrimitive.Description>) {
  const { className, ...props } = alertDialogDescriptionProps;

  return <AlertDialogPrimitive.Description data-slot="alert-dialog-description" className={cn('text-sm', className)} {...props} />;
}

export function AlertDialogBody(alertDialogBodyProps: ComponentProps<'div'>) {
  const { className, ...props } = alertDialogBodyProps;

  return <div data-slot="dialog-body" className={cn('relative contents flex-col space-y-0 text-left', className)} {...props} />;
}

export function AlertDialogAction(alertDialogActionProps: ComponentProps<typeof AlertDialogPrimitive.Action> & ButtonProps) {
  const { className, size, variant = 'primary', variantColor = 'default', ...props } = alertDialogActionProps;

  return <AlertDialogPrimitive.Action className={cn(buttonVariants({ size, variant, variantColor }), className)} {...props} />;
}

export function AlertDialogCancel(alertDialogCancelProps: ComponentProps<typeof AlertDialogPrimitive.Cancel> & ButtonProps) {
  const { className, size, variant = 'tertiary', variantColor = 'default', ...props } = alertDialogCancelProps;

  return <AlertDialogPrimitive.Cancel className={cn(buttonVariants({ size, variant, variantColor }), className)} {...props} />;
}
